import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

const STATUS_COLORS = {
  PENDING_ACCEPTANCE: "#3b82f6", AWAITING_PAYMENT: "#3b82f6", FUNDED: "#7c3aed",
  IN_PROGRESS: "#7c3aed", DELIVERED: "#16a34a", DISPUTED: "#d97706",
  COMPLETED: "#16a34a", CANCELED: "#6b7280", REFUNDED: "#dc2626",
};

const STATUS_LABELS = {
  PENDING_ACCEPTANCE: "Pending", AWAITING_PAYMENT: "Awaiting Payment",
  FUNDED: "Paid", IN_PROGRESS: "Awaiting Delivery",
  DELIVERED: "Delivered", DISPUTED: "Disputed", COMPLETED: "Completed",
  CANCELED: "Canceled", REFUNDED: "Refunded",
};

export default function OrdersPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const loadData = useCallback(async () => {
    try {
      const [ordersRes, offersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/orders`, { headers: authHeaders() }),
        fetch(`${API_BASE_URL}/offers`, { headers: authHeaders() }),
      ]);
      const ordersData = await ordersRes.json();
      const offersData = await offersRes.json();
      if (ordersRes.ok) {
        const all = Array.isArray(ordersData) ? ordersData : [];
        setOrders(all.filter(o => o.buyerId === user?.id || o.sellerId === user?.id));
      }
      if (offersRes.ok) setOffers(Array.isArray(offersData) ? offersData : []);
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  function getOrderTitle(order) {
    if (order.items?.length > 0) {
      const names = order.items.map(i => i.listingItem?.title).filter(Boolean);
      if (names.length === 1) return names[0];
      if (names.length === 2) return `${names[0]} + ${names[1]}`;
      if (names.length > 2) return `${names[0]} + ${names.length - 1} more`;
    }
    return order.listing?.title || "Order";
  }

  function getOrderPhoto(order) {
    for (const item of order.items || []) {
      const photos = item.listingItem?.photoUrls;
      if (photos?.length > 0) return photos[0];
    }
    return null;
  }

  const myOffers = offers.filter(o => o.buyerId === user?.id);

  const getFilteredOrders = () => {
    if (filter === "buying") return orders.filter(o => o.buyerId === user?.id);
    if (filter === "selling") return orders.filter(o => o.sellerId === user?.id);
    if (filter === "active") return orders.filter(o => !["COMPLETED", "CANCELED", "REFUNDED"].includes(o.status));
    if (filter === "completed") return orders.filter(o => ["COMPLETED", "DELIVERED"].includes(o.status));
    if (filter === "offers") return [];
    return orders;
  };

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 760, margin: "0 auto", padding: "24px 20px" },
    title: { fontSize: 26, fontWeight: 800, marginBottom: 4, color: "#0f172a" },
    subtitle: { fontSize: 14, color: "#64748b", marginBottom: 20 },
    filterRow: { display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" },
    filterBtn: (active) => ({ padding: "7px 14px", borderRadius: 999, border: "1px solid", borderColor: active ? "#3b82f6" : "rgba(59,130,246,0.2)", background: active ? "#3b82f6" : "rgba(255,255,255,0.7)", color: active ? "#fff" : "#475569", cursor: "pointer", fontSize: 13, fontWeight: 600 }),
    card: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 14, padding: "14px 16px", marginBottom: 10, cursor: "pointer", display: "flex", gap: 14, alignItems: "center", backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)", transition: "box-shadow 0.2s, transform 0.15s" },
    photo: { width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid rgba(59,130,246,0.12)" },
    photoPlaceholder: { width: 56, height: 56, borderRadius: 10, background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(124,58,237,0.1))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 },
    cardBody: { flex: 1, minWidth: 0 },
    cardRole: { fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 },
    cardTitle: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    cardId: { fontSize: 11, color: "#94a3b8", marginBottom: 3 },
    cardRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 },
    amount: { fontSize: 16, fontWeight: 800, color: "#1e40af" },
    badge: (status) => ({ padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: `${STATUS_COLORS[status] || "#6b7280"}15`, color: STATUS_COLORS[status] || "#6b7280", border: `1px solid ${STATUS_COLORS[status] || "#6b7280"}30`, whiteSpace: "nowrap" }),
    disputeDot: { width: 8, height: 8, borderRadius: "50%", background: "#d97706", flexShrink: 0 },
    actionNeeded: { fontSize: 10, color: "#d97706", fontWeight: 700 },
    empty: { textAlign: "center", padding: "60px 20px", color: "#94a3b8" },
    offerCard: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 14, padding: "14px 16px", marginBottom: 10, display: "flex", gap: 14, alignItems: "center", backdropFilter: "blur(8px)", boxShadow: "0 2px 8px rgba(59,130,246,0.06)" },
  };

  if (loading) return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", color: "#94a3b8" }}>Loading...</div>
    </div>
  );

  const filteredOrders = getFilteredOrders();

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.main}>
        <div style={s.title}>My Orders</div>
        <div style={s.subtitle}>{orders.length} orders · {myOffers.length} offers</div>

        <div style={s.filterRow}>
          {[
            { key: "all", label: "All" },
            { key: "buying", label: "Buying" },
            { key: "selling", label: "Selling" },
            { key: "active", label: "Active" },
            { key: "completed", label: "Completed" },
            { key: "offers", label: "My Offers" },
          ].map(f => (
            <button key={f.key} style={s.filterBtn(filter === f.key)} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
        </div>

        {filter !== "offers" && (
          filteredOrders.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No orders found</div>
              <div>Try a different filter</div>
            </div>
          ) : filteredOrders.map(order => {
            const isBuyer = order.buyerId === user?.id;
            const isSeller = order.sellerId === user?.id;
            const orderTitle = getOrderTitle(order);
            const orderPhoto = getOrderPhoto(order);
            const hasActiveDispute = order.dispute && !["RESOLVED", "CLOSED"].includes(order.dispute?.status);
            const needsAction =
              (isBuyer && ["PENDING_ACCEPTANCE", "AWAITING_PAYMENT"].includes(order.status) && !order.payment) ||
              (isBuyer && order.sellerConfirmed && !order.buyerConfirmed) ||
              (isSeller && ["FUNDED", "IN_PROGRESS"].includes(order.status) && !order.sellerConfirmed);

            return (
              <div
                key={order.id}
                style={s.card}
                onClick={() => navigate(`/orders/${order.id}`)}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 6px 20px rgba(59,130,246,0.15)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 2px 8px rgba(59,130,246,0.06)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                {orderPhoto ? (
                  <img src={orderPhoto} alt={orderTitle} style={s.photo} onError={e => e.target.style.display = "none"} />
                ) : (
                  <div style={s.photoPlaceholder}>📦</div>
                )}
                <div style={s.cardBody}>
                  <div style={s.cardRole}>{isBuyer && isSeller ? "YOUR LISTING" : isBuyer ? "BUYING" : "SELLING"}</div>
                  <div style={s.cardTitle}>{orderTitle}</div>
                  <div style={s.cardId}>#{order.id.slice(0, 8)}</div>
                  {needsAction && <div style={s.actionNeeded}>⚡ Action needed</div>}
                  {hasActiveDispute && <div style={{ fontSize: 10, color: "#d97706", fontWeight: 700 }}>⚠️ Dispute open</div>}
                </div>
                <div style={s.cardRight}>
                  <div style={s.amount}>${Number(order.totalAmount).toFixed(2)}</div>
                  <span style={s.badge(order.status)}>{STATUS_LABELS[order.status] || order.status.replace(/_/g, " ")}</span>
                  <span style={{ color: "#94a3b8", fontSize: 16 }}>›</span>
                </div>
              </div>
            );
          })
        )}

        {filter === "offers" && (
          myOffers.length === 0 ? (
            <div style={s.empty}><div style={{ fontSize: 48, marginBottom: 16 }}>💬</div><div>No offers yet</div></div>
          ) : myOffers.map(offer => (
            <div key={offer.id} style={s.offerCard}>
              <div style={s.photoPlaceholder}>💬</div>
              <div style={s.cardBody}>
                <div style={s.cardRole}>OFFER</div>
                <div style={s.cardTitle}>{offer.listing?.title || "Offer"}</div>
                <div style={s.cardId}>To {offer.seller?.fullName || "Seller"}</div>
              </div>
              <div style={s.cardRight}>
                <div style={s.amount}>${Number(offer.totalAmount).toFixed(2)}</div>
                <span style={{ padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 700, background: "rgba(217,119,6,0.12)", color: "#d97706" }}>{offer.status}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}